<script setup lang="ts">
import { getCategoryRoute, getTranslatedProperty } from "@shopware/helpers";
import type { Schemas } from "#shopware";
import { onKeyStroke, useActiveElement } from "@vueuse/core";
import { useCachedCmsElementsFromCategoryWithChilds } from "~/components/server/useCachedCmsElementsFromCategoryWithChilds";
import type { RefSymbol } from "@vue/reactivity";
type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const { navigationElements } = useNavigation();
const currentMenuPosition = ref<string | undefined>(undefined);
const resetActiveClass = ref<boolean>(true);

const emit = defineEmits({
  close: () => {
    return true;
  },
});

const route = useRoute();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const categoryId = "01912be66e51779e8063bc45d89c6487";
const categoryData =
  await useCachedCmsElementsFromCategoryWithChilds(categoryId);
// const subCategories = categoryData[0]?.children;

onMounted(() => {
  let currentNaviagtionElement: NavigationElement | undefined;
  if (navigationElements.value) {
    currentNaviagtionElement = findNavigationElement(
      route.path.slice(1),
      navigationElements.value
    );
  }
  if (currentNaviagtionElement) {
    updateActiveClass(
      currentNaviagtionElement.id,
      currentNaviagtionElement.parentId
    );
  }

  // Set the initial active tab to the active brand
  if (activeBrand.value?.id) {
    currentMenuPosition.value = activeBrand.value.id;
  }

  setMenuSize();
  window.addEventListener("resize", setMenuSize);
});

const findNavigationElement = (
  routePath: string,
  navigation: NavigationElement[]
): NavigationElement | undefined => {
  for (let i = 0; i < navigation.length; i++) {
    const navigationElement = navigation[i];
    const seoUrls = navigationElement.seoUrls as
      | Schemas["SeoUrl"][]
      | undefined;
    if (seoUrls) {
      for (let j = 0; j < seoUrls.length; j++) {
        const currentSeoUrl = seoUrls[j];
        if (currentSeoUrl && currentSeoUrl.seoPathInfo === routePath) {
          return navigationElement;
        }
      }
    }
    const children = navigationElement.children;
    if (children) {
      const foundElement = findNavigationElement(routePath, children);
      if (foundElement) {
        return foundElement;
      }
    }
  }
  return undefined;
};

const onUpdateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  updateActiveClass(navigationId, parentId);
};

const resetNavigationActiveClass = (navigation: NavigationElement[]) => {
  for (let ni = 0; ni < navigation.length; ++ni) {
    navigation[ni].activeClass = false;
    const children = navigation[ni].children;
    if (children) {
      resetNavigationActiveClass(children);
    }
  }
};

const updateActiveClass = (
  navigationId: string,
  parentId: string | undefined
) => {
  const setNavigationActiveClass = (
    navigation: NavigationElement[],
    navigationId: string,
    parentId: string | undefined
  ) => {
    for (let ni = 0; ni < navigation.length; ++ni) {
      if (navigation[ni].id === navigationId) {
        navigation[ni].activeClass = true;
      }
      if (navigation[ni].id == parentId) {
        navigation[ni].activeClass = true;
        if (navigationElements.value) {
          setNavigationActiveClass(
            navigationElements.value,
            navigationId,
            navigation[ni].parentId
          );
        }
      }
      const children = navigation[ni].children;
      if (children) {
        setNavigationActiveClass(children, navigationId, parentId);
      }
    }
  };

  if (navigationElements.value) {
    resetNavigationActiveClass(navigationElements.value);
    setNavigationActiveClass(navigationElements.value, navigationId, parentId);
    resetActiveClass.value = false;
  }
};
//#region - keyboard navigation (highliy connected to the HTML structure)
const activeElement = useActiveElement();
onKeyStroke("ArrowDown", (e) => {
  // next parent link or next child link
  if (
    activeElement.value?.parentElement?.nextElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value.parentElement.nextElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

onKeyStroke("ArrowUp", (e) => {
  // previous parent link or previous child link
  if (
    activeElement.value?.parentElement?.previousElementSibling?.getElementsByTagName(
      "a"
    )[0]
  ) {
    e.preventDefault();
    activeElement.value?.parentElement.previousElementSibling
      .getElementsByTagName("a")[0]
      .focus();
    return;
  }
});

//#endregion - keyboard navigation

// reset when route.path changes
watch(
  () => route.path,
  () => {
    if (resetActiveClass.value == true && navigationElements.value) {
      resetNavigationActiveClass(navigationElements.value);
    }
    resetActiveClass.value = true;
    currentMenuPosition.value = undefined;
    emit("close");
  }
);

const setMenuSize = () => {
  const menuContainer = document.querySelector(".main-menu .container");
  const spanBefore = document.querySelectorAll(".main-menu-bg .span-before");
  const spanAfter = document.querySelectorAll(".main-menu-bg .span-after");

  if (menuContainer && spanBefore && spanAfter) {
    const menuSize = (window.innerWidth - menuContainer.clientWidth) / 2 + 15;

    for (var i = 0; i < spanBefore.length; i++) {
      (spanBefore[i] as HTMLElement).style.width = menuSize + "px";
      (spanAfter[i] as HTMLElement).style.width = menuSize + "px";
    }
  }
};

// Before unmount equivalent
onBeforeUnmount(() => {
  window.removeEventListener("resize", setMenuSize);
});

const { subBrands, activeBrand, defaultBrand, brands, activeCategoryId } =
  useBrands();

const activeCategory = ref<string | null>(null);
activeCategory.value = activeCategoryId.value;
const findActiveBrand = (elements: NavigationElement[]) => {
  if (!activeCategoryId.value) {
    return elements[0];
  }
  for (const element of elements) {
    if (
      element.id === activeCategory.value ||
      hasActiveChild(element.children || [])
    ) {
      return element;
    }
  }
  return elements[0];
};

const hasActiveChild = (children: NavigationElement[]): boolean => {
  return children.some(
    (child) =>
      child.id === activeCategory.value || hasActiveChild(child.children || [])
  );
};

const activeBrandNavigation = computed(() => {
  return findActiveBrand(brands.value || []);
});

const toggleSubmenu = (navigationId: string) => {
  if (currentMenuPosition.value === navigationId) {
    currentMenuPosition.value = undefined;
  } else {
    currentMenuPosition.value = navigationId;
  }
};

const subCategories = computed(
  () =>
    activeBrandNavigation.value?.children.find(
      (element) =>
        element.translated?.customFields?.custom_new_and_trendy_enabled
    )?.children || []
);

watch(
  () => activeBrand.value,
  () => {
    currentMenuPosition.value = activeBrand.value?.id;
  }
);

watch(
  () => route.path,
  () => {
    currentMenuPosition.value = activeBrand.value?.id;
  }
);
</script>
<template>
  <!-- eslint-disable vue/no-v-html -->
  <div class="navigation-wrapper">
    <!-- Level One Categories as Horizontal Tabs -->
    <div class="overflow-x-auto bg-white border-b border-gray-200">
      <ul class="flex flex-row gap-2 px-0 m-0 list-none overflow-x-auto">
        <li
          v-for="(navigationElement, index) in brands?.sort(
            (a: any, b: any) => {
              const aHasChildren = a.children && a.children.length > 0 ? 1 : 0;
              const bHasChildren = b.children && b.children.length > 0 ? 1 : 0;
              return bHasChildren - aHasChildren;
            }
          )"
          class="flex-1"
          :key="navigationElement.id"
          :class="[
            'py-3 px-5 cursor-pointer opacity-50 border-b-2 border-transparent bg-white rounded-t-lg transition-all duration-200 font-medium text-gray-800 flex items-center justify-center',
            {
              'bg-white !opacity-100 border-b-2 !border-gray-800 text-gray-800 font-bold':
                (activeBrandNavigation?.id === navigationElement.id &&
                  currentMenuPosition === activeBrandNavigation?.id) ||
                (activeBrandNavigation?.id !== navigationElement.id &&
                  currentMenuPosition !== activeBrandNavigation?.id),
            },
          ]"
          @click="
            currentMenuPosition = navigationElement.id;
            activeCategory = navigationElement.id;
          "
        >
          <span class="">
            <NuxtImg
              v-if="navigationElement.media?.url"
              loading="lazy"
              format="webp"
              width="120px"
              height="26"
              preload
              :src="navigationElement.media?.url"
              :alt="navigationElement.media?.translated.alt"
              densities="x1"
              class="h-auto w-auto max-h-8 object-contain"
            />
            <span v-else>{{
              getTranslatedProperty(navigationElement, "name")
            }}</span>
          </span>
        </li>
      </ul>
    </div>
    <!-- Subcategories of the selected tab -->
    <div
      class="subcategories-container"
      :class="
        defaultBrand?.name
          ? `brand-${defaultBrand.name.toLowerCase().replace(/\s+/g, '-')}`
          : ''
      "
    >
      <template v-for="(navigationElement, index) in subBrands">
        <div
          v-if="
            navigationElement.id === currentMenuPosition &&
            navigationElement.children &&
            navigationElement.children.length > 0
          "
          :key="'subcat-' + navigationElement.id"
          class="sub-menu-container"
          :class="`parent-category-${getTranslatedProperty(navigationElement, 'name').toLowerCase().replace(/\s+/g, '-')}`"
        >
          <ul
            class="sub-menu py-1 mt-0 bg-white text-black03 w-full md:flex justify-between main-menu-bg"
          >
            <li class="w-full py-1">
              <ul class="relative flex flex-col gap-3 sub-menu-in">
                <ul
                  class="top-0 w-full border-[#D9D9D9] px-4 flex flex-wrap gap-0 !m-0"
                >
                  <li
                    class="menu-line flex items-center justify-between w-full pl-0"
                  >
                    <ul
                      class="flex flex-row flex-wrap gap-0 pl-0 [&_li]:w-1/2 -mx-5"
                    >
                      <template v-if="navigationElement.children.length > 0">
                        <LayoutTopNavigationRecursiveMobile
                          :navigation-element-children="
                            navigationElement.children
                          "
                          @update-active-class="onUpdateActiveClass"
                          @focusout-last-element="
                            currentMenuPosition = undefined
                          "
                        />
                      </template>
                      <template v-if="subCategories.length > 0">
                        <LayoutTopNavigationRecursiveMobile
                          :navigation-element-children="subCategories"
                          @update-active-class="onUpdateActiveClass"
                          @focusout-last-element="
                            currentMenuPosition = undefined
                          "
                        />
                      </template>
                    </ul>
                  </li>
                </ul>
              </ul>
            </li>
          </ul>
        </div>
      </template>
    </div>
    <ul
      class="flex mb-2 flex-wrap md:flex items-center gap-y-2 text-sm lg:text-base main-menu-list pt-2 main-ul"
      :class="{
        hidden: currentMenuPosition && activeBrand?.id !== currentMenuPosition,
      }"
    >
      <!-- Items without subcategories (regular li elements) -->
      <template
        v-for="(navigationElement, index) in activeBrand?.children?.filter(
          (element) => !element.children || element.children.length === 0
        )"
        :key="navigationElement.id"
        @mouseover="
          currentMenuPosition = navigationElement.id;
          activeCategory = navigationElement.id;
        "
        @focusin="
          currentMenuPosition = navigationElement.id;
          activeCategory = navigationElement.id;
        "
      >
        <li
          v-if="
            !navigationElement.translated?.customFields
              ?.custom_new_and_trendy_enabled
          "
          class="menu-line flex items-center justify-between w-1/2 order-2"
          :class="{
            active: (navigationElement as NavigationElement).activeClass,
            '!text-red-500 ':
              getTranslatedProperty(navigationElement, 'name') === 'Sale',
          }"
          @mouseover="setMenuSize"
        >
          <NuxtLink
            role="menuitem"
            :target="
              navigationElement.externalLink || navigationElement.linkNewTab
                ? '_blank'
                : ''
            "
            :to="formatLink(getCategoryRoute(navigationElement))"
            class="uppercase !py-0 w-full text-white text-center"
            :class="{
              '!bg-red-500 ':
                getTranslatedProperty(navigationElement, 'name') === 'Sale',
            }"
            @click="
              updateActiveClass(
                navigationElement.id,
                navigationElement.parentId
              )
            "
          >
            <div class="flex items-center w-full flex-col">
              <NuxtImg
                v-if="navigationElement.media?.url"
                loading="lazy"
                format="webp"
                :src="navigationElement.media?.url"
                :alt="
                  navigationElement.media?.translated?.alt ||
                  getTranslatedProperty(navigationElement, 'name')
                "
                densities="x1"
                class="w-full h-[100px] object-cover"
              />
              <div
                v-else
                class="h-[100px] w-full bg-black03"
                :class="{
                  '!bg-red-500 ':
                    getTranslatedProperty(navigationElement, 'name') === 'Sale',
                }"
              ></div>
              <span
                class="text-center bg-black03 w-full text-white font-bold flex px-4 py-3 items-center justify-start"
                :class="{
                  '!bg-red-500 !text-white':
                    getTranslatedProperty(navigationElement, 'name') === 'Sale',
                }"
              >
                {{ getTranslatedProperty(navigationElement, "name") }}
              </span>
            </div>
          </NuxtLink>
        </li>
      </template>

      <!-- Subcategories from activeBrand children -->
      <template
        v-for="(navigationElement, index) in subCategories.filter(
          (element) => !element.children || element.children.length === 0
        )"
        :key="navigationElement.id"
      >
        <li
          v-if="
            !navigationElement.translated?.customFields
              ?.custom_new_and_trendy_enabled
          "
          class="menu-line flex items-center justify-between w-1/2 order-2"
          :class="{
            active: (navigationElement as NavigationElement).activeClass,
            '!text-red-500 ':
              getTranslatedProperty(navigationElement, 'name') === 'Sale',
          }"
          @mouseover="setMenuSize"
        >
          <NuxtLink
            role="menuitem"
            :target="
              navigationElement.externalLink || navigationElement.linkNewTab
                ? '_blank'
                : ''
            "
            :to="formatLink(getCategoryRoute(navigationElement))"
            class="uppercase !py-0 w-full text-white text-center"
            :class="{
              '!bg-red-500 ':
                getTranslatedProperty(navigationElement, 'name') === 'Sale',
            }"
            @click="
              updateActiveClass(
                navigationElement.id,
                navigationElement.parentId
              )
            "
          >
            <div class="flex items-center w-full flex-col">
              <NuxtImg
                v-if="navigationElement.media?.url"
                loading="lazy"
                format="webp"
                :src="navigationElement.media?.url"
                :alt="
                  navigationElement.media?.translated?.alt ||
                  getTranslatedProperty(navigationElement, 'name')
                "
                densities="x1"
                class="w-full h-[100px] object-cover"
              />
              <div
                v-else
                class="h-[100px] w-full bg-black03"
                :class="{
                  '!bg-red-500 ':
                    getTranslatedProperty(navigationElement, 'name') === 'Sale',
                }"
              ></div>
              <span
                class="text-center bg-black03 w-full text-white font-bold flex px-4 py-3 items-center justify-start"
                :class="{
                  '!bg-red-500 !text-white':
                    getTranslatedProperty(navigationElement, 'name') === 'Sale',
                }"
              >
                {{ getTranslatedProperty(navigationElement, "name") }}
              </span>
            </div>
          </NuxtLink>
        </li>
      </template>
      <!-- Single div wrapper for all items with subcategories -->
      <div
        v-if="
          activeBrand?.children?.some(
            (child) =>
              !child.translated?.customFields?.custom_new_and_trendy_enabled &&
              child.children &&
              child.children.length > 0
          ) ||
          subCategories.some(
            (child) =>
              !child.translated?.customFields?.custom_new_and_trendy_enabled &&
              child.children &&
              child.children.length > 0
          )
        "
        class="subcategories-container order-1 flex flex-wrap w-full"
      >
        <!-- Active Brand Children with subcategories -->
        <li
          v-for="navigationElement in activeBrand?.children?.filter(
            (element) =>
              !element.translated?.customFields
                ?.custom_new_and_trendy_enabled &&
              element.children &&
              element.children.length > 0
          )"
          :key="navigationElement.id"
          class="menu-line flex items-center justify-between w-1/2"
          :class="{
            active: (navigationElement as NavigationElement).activeClass,
            '!text-red-500 ':
              getTranslatedProperty(navigationElement, 'name') === 'Sale',
          }"
          @mouseover="
            currentMenuPosition = navigationElement.id;
            activeCategory = navigationElement.id;
            setMenuSize;
          "
          @focusin="
            currentMenuPosition = navigationElement.id;
            activeCategory = navigationElement.id;
          "
        >
          <div
            class="category-link uppercase cursor-pointer flex items-center gap-3 w-full font-bold py-2 px-4"
            @click="toggleSubmenu(navigationElement.id)"
          >
            <span>{{ getTranslatedProperty(navigationElement, "name") }}</span>
            <svg
              class="w-4 h-4 transition-transform duration-200"
              :class="{
                'rotate-90': currentMenuPosition === navigationElement.id,
              }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </li>

        <!-- SubCategories with subcategories -->
        <li
          v-for="navigationElement in subCategories.filter(
            (element) =>
              !element.translated?.customFields
                ?.custom_new_and_trendy_enabled &&
              element.children &&
              element.children.length > 0
          )"
          :key="navigationElement.id"
          class="menu-line flex items-center justify-between w-1/2"
          :class="{
            active: (navigationElement as NavigationElement).activeClass,
            '!text-red-500 ':
              getTranslatedProperty(navigationElement, 'name') === 'Sale',
          }"
          @mouseover="setMenuSize"
        >
          <div
            class="category-link uppercase cursor-pointer flex items-center gap-3 w-full font-bold py-2 px-4"
            @click="toggleSubmenu(navigationElement.id)"
          >
            <span>{{ getTranslatedProperty(navigationElement, "name") }}</span>
            <svg
              class="w-4 h-4 transition-transform duration-200"
              :class="{
                'rotate-90': currentMenuPosition === navigationElement.id,
              }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </li>
      </div>
    </ul>

    <!-- Subcategories outside the main navigation -->
    <div class="subcategories-container">
      <!-- Active Brand Children Subcategories -->
      <template
        v-for="(navigationElement, index) in activeBrand?.children"
        :key="`sub-${navigationElement.id}`"
      >
        <client-only>
          <div
            v-show="
              navigationElement.children.length > 0 &&
              currentMenuPosition === navigationElement.id
            "
            class="sub-menu-container fixed top-[100px] left-0 w-full h-full bg-white z-50"
            :class="`parent-category-${getTranslatedProperty(navigationElement, 'name').toLowerCase().replace(/\s+/g, '-')}`"
          >
            <div
              class="flex items-center gap-2 px-4 py-3 bg-gray-100 border-b border-gray-200 cursor-pointer font-medium text-gray-600 hover:bg-gray-200 transition-colors duration-200"
              @click="currentMenuPosition = undefined"
            >
              <i class="fa fa-angle-left text-base"></i> Zurück
            </div>

            <ul
              class="sub-menu pt-5 py-1 mt-0 bg-white text-black03 w-full md:flex justify-between main-menu-bg h-[calc(100vh-180px)] overflow-y-auto"
            >
              <div class="parent-category-header px-5 mb-3 mt-1">
                <NuxtLink
                  class="parent-category-header font-semibold uppercase"
                  :to="formatLink(getCategoryRoute(navigationElement))"
                >
                  {{ getTranslatedProperty(navigationElement, "name") }}
                </NuxtLink>
              </div>
              <li class="w-full py-1">
                <ul class="relative flex flex-col gap-3 sub-menu-in">
                  <ul
                    class="top-0 w-full w-sa border-[#D9D9D9] px-4 flex flex-wrap gap-1 [&_li>div>div>div]:hidden [&_li>div>div]:bg-white [&_li>div>div>a]:text-black03 [&_li>div>div>a]:py-2 [&_li>div>div>a]:px-0 [&_li>div>div>a]:capitalize [&_li>div>div>a]:text-[16px] [&_li>div>div>a]:font-medium [&_li>ul]:gap-1"
                  >
                    <li class="w-full">
                      <ul class="flex flex-col gap-4">
                        <template v-if="navigationElement.children.length > 0">
                          <LayoutTopNavigationRecursiveMobile
                            :navigation-element-children="
                              navigationElement.children
                            "
                            @update-active-class="onUpdateActiveClass"
                            @focusout-last-element="
                              currentMenuPosition = undefined
                            "
                          />
                        </template>
                        <template v-if="subCategories.length > 0">
                          <LayoutTopNavigationRecursiveMobile
                            :navigation-element-children="subCategories"
                            @update-active-class="onUpdateActiveClass"
                            @focusout-last-element="
                              currentMenuPosition = undefined
                            "
                          />
                        </template>
                      </ul>
                    </li>
                  </ul>
                </ul>
              </li>
            </ul>
          </div>
        </client-only>
      </template>

      <!-- Sub Categories Subcategories -->
      <template
        v-for="(navigationElement, index) in subCategories"
        :key="`subcat-${navigationElement.id}`"
      >
        <client-only>
          <div
            v-show="
              navigationElement.children &&
              navigationElement.children.length > 0 &&
              currentMenuPosition === navigationElement.id
            "
            class="sub-menu-container"
            :class="`parent-category-${getTranslatedProperty(navigationElement, 'name').toLowerCase().replace(/\s+/g, '-')}`"
          >
            <div
              class="flex items-center gap-2 px-4 py-3 bg-gray-100 border-b border-gray-200 cursor-pointer font-medium text-gray-600 hover:bg-gray-200 transition-colors duration-200"
              @click="currentMenuPosition = undefined"
            >
              <i class="fa fa-angle-left text-base"></i> Zurück
            </div>
            <div class="parent-category-header px-4">
              <NuxtLink
                class="parent-category-header font-bold"
                :to="formatLink(getCategoryRoute(navigationElement))"
              >
                {{ getTranslatedProperty(navigationElement, "name") }}
              </NuxtLink>
            </div>
            <ul
              class="sub-menu py-3 bg-white text-black03 w-full md:flex justify-between main-menu-bg main-ul"
              :class="{
                'active-tab':
                  currentMenuPosition &&
                  activeBrand?.id === currentMenuPosition,
              }"
            >
              <li class="w-full py-1">
                <ul class="relative flex flex-col gap-3 sub-menu-in">
                  <ul
                    class="top-0 w-full w-sa border-[#D9D9D9] ps-8 flex gap-3"
                  >
                    <li class="w-full">
                      <ul class="flex flex-col gap-2">
                        <template
                          v-if="
                            navigationElement.children &&
                            navigationElement.children.length > 0
                          "
                        >
                          <LayoutTopNavigationRecursiveMobile
                            :navigation-element-children="
                              navigationElement.children
                            "
                            @update-active-class="onUpdateActiveClass"
                            @focusout-last-element="
                              currentMenuPosition = undefined
                            "
                          />
                        </template>
                        <template v-if="subCategories.length > 0">
                          <LayoutTopNavigationRecursiveMobile
                            :navigation-element-children="subCategories"
                            @update-active-class="onUpdateActiveClass"
                            @focusout-last-element="
                              currentMenuPosition = undefined
                            "
                          />
                        </template>
                      </ul>
                    </li>
                  </ul>
                </ul>
              </li>
            </ul>
          </div>
        </client-only>
      </template>
    </div>
  </div>
</template>
<style scoped>
.menu-line.active {
  filter: grayscale(100%);
}

.menu-line .link-active{
  filter: grayscale(100%);
}
.menu-line .link-active span.bg-black03{
  background: #757574;
}
.menu-line.active span.bg-black03{
  background: #757574;
}
</style>